import React from 'react';
import { useSelector } from 'react-redux';
import { navigate } from 'react-schedule-calendar/lib/utils/constants';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/icons-material/Menu';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
import WifiOff from '@mui/icons-material/WifiOff';
import SignalWifi1Bar from '@mui/icons-material/SignalWifi1Bar';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import * as selectors from '../store/selectors';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  label: {
    flex: '5 0 0',
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.button.fontSize,
    },
  },
  space: {
    '&:not(:last-child)': {
      marginRight: theme.spacing(1.5),
    },
  },
  activityIndicator: {
    marginRight: theme.spacing(1),
  },
}));

type ToolbarProps = {
  view: string;
  label: string;
  onViewChange?: (view: string) => void;
  onNavigate?: (action: string) => void;
  onDrawerOpen?: () => void;
};

const Toolbar = ({
  view,
  label,
  onViewChange,
  onNavigate,
  onDrawerOpen,
}: ToolbarProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isLoading = useSelector(selectors.showActivityIndicator);
  const networkStatus = useSelector(selectors.getNetworkStatus);

  const handleViewChange = () => {
    onViewChange?.('month');
  };

  const handleNavigate = (action: string) => {
    onNavigate?.(action);
  };

  const handleDrawerOpen = () => {
    onDrawerOpen?.();
  };

  return (
    <div className={classes.container}>
      <IconButton onClick={handleDrawerOpen} size="large">
        <Menu />
      </IconButton>
      <Button
        variant="outlined"
        size="small"
        onClick={() => handleNavigate(navigate.TODAY)}
        className={classes.space}
      >
        Today
      </Button>
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
        <IconButton
          onClick={() => handleNavigate(navigate.PREVIOUS)}
          size="large"
        >
          <ChevronLeft />
        </IconButton>
        <IconButton
          onClick={() => handleNavigate(navigate.NEXT)}
          className={classes.space}
          size="large"
        >
          <ChevronRight />
        </IconButton>
      </Box>
      <Typography variant="h6" className={classes.label}>
        {label}
      </Typography>

      {networkStatus === 'offline' && (
        <Tooltip title={t('offline')}>
          <WifiOff color="error" className={classes.activityIndicator} />
        </Tooltip>
      )}
      {networkStatus === 'slow' && (
        <Tooltip title={t('slowConnection')}>
          <SignalWifi1Bar
            color="disabled"
            className={classes.activityIndicator}
          />
        </Tooltip>
      )}
      {isLoading && (
        <CircularProgress size={20} className={classes.activityIndicator} />
      )}
      {view !== 'month' && (
        <IconButton onClick={handleViewChange} size="large">
          <Close />
        </IconButton>
      )}
    </div>
  );
};

export default Toolbar;
